import { useEffect, useState, useCallback, useRef } from "react";
import MainLayout from "@/layouts/MainLayout";
import AuthService from "@/lib/auth";
import { Card } from "@/components/ui/card";
import {
  Loader2, Search, Plus, MapPin, Phone, Mail,
  Building2, Edit2, Trash2, ChevronRight, ChevronDown, Save
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";

// ── Types ──────────────────────────────────────────────────────────────────────

interface StateOption {
  state_id:   string;
  state_name: string;
}

// Per-branch state kept in React.
// state_id and phone need React control (Select + auto-formatter).
// All other text fields use uncontrolled inputs (defaultValue) and are read
// from the DOM at save time via readFormValues().
interface BranchState {
  id?:           string;   // present for existing DB branches
  name:          string;
  state_id:      string;
  phone:         string;
  // Address fields — populated from DB (old schema uses `address`; new schema
  // uses split fields). Stored here so defaultValue props pre-fill on edit.
  address?:      string;   // legacy single-column from old DB rows
  address_line1?: string;
  address_line2?: string;
  city?:         string;
  zip?:          string;
  email?:        string;
}

interface HospitalForm {
  id?:      string;
  name:     string;
  branches: BranchState[];
}

// Hospital as displayed in the card list
interface Hospital {
  id:       string;
  name:     string;
  phone:    string;
  email:    string;
  address:  string;
  branches?: {
    id:            string;
    name:          string;
    phone:         string;
    email:         string;
    address:       string;   // legacy single-column (may be empty for new-schema rows)
    address_line1?: string;
    address_line2?: string;
    city?:          string;
    zip?:           string;
    state_id?:      string;
  }[];
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatPhoneInput(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length > 6) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return digits;
}

function isValidPhone(phone: string): boolean {
  return phone.replace(/\D/g, "").length === 10;
}

function isValidZip(zip: string): boolean {
  return /^\d{5}(-\d{4})?$/.test(zip.trim());
}

function emptyHospital(): HospitalForm {
  return { name: "", branches: [] };
}

// Convert an API branch row into BranchState for the edit modal.
// Strips the +1 country code and re-formats to ###-###-#### for the phone input.
function branchStateFromApi(b: NonNullable<Hospital["branches"]>[number]): BranchState {
  const rawPhone = (b.phone || "")
    .replace(/^\+1/, "")
    .replace(/\D/g, "")
    .replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
  return {
    id:            b.id,
    name:          b.name          || "",
    state_id:      b.state_id      || "",
    phone:         rawPhone,
    // Legacy single-column: pre-fills Address Line 1 for old-schema rows
    address:       b.address       || "",
    // New split fields: pre-fill directly when present (new-schema rows)
    address_line1: b.address_line1 || "",
    address_line2: b.address_line2 || "",
    city:          b.city          || "",
    zip:           b.zip           || "",
    email:         b.email         || "",
  };
}

// ── Component ──────────────────────────────────────────────────────────────────

/** Build a displayable single-line address from split or legacy fields. */
function formatBranchAddress(b: {
  address?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  zip?: string;
  state_name?: string;
}): string {
  const line1          = b.address_line1 || b.address || "";
  const line2          = b.address_line2 || "";
  const cityStateZip   = [b.city, b.state_name, b.zip].filter(Boolean).join(", ");
  return [line1, line2, cityStateZip].filter(Boolean).join(", ");
}

export default function HospitalManagement() {
  const [hospitals, setHospitals]   = useState<Hospital[]>([]);
  const [states, setStates]         = useState<StateOption[]>([]);
  const [loading, setLoading]       = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [userRole, setUserRole]     = useState<string | null>(null);
  const [expandedHospitalId, setExpandedHospitalId] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [current, setCurrent]       = useState<HospitalForm>(emptyHospital());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Incremented on every modal open so DialogContent remounts fully each time,
  // forcing uncontrolled inputs (defaultValue) to pick up fresh branch data.
  const [modalKey, setModalKey]     = useState(0);

  // Ref to the modal div — lets readFormValues() grab uncontrolled input values
  const formRef = useRef<HTMLDivElement>(null);

  const isBranchAdmin = (userRole || "").includes("branch");

  // ── Load states on mount ───────────────────────────────────────────────────
  useEffect(() => {
    AuthService.getStates().then(setStates).catch(() => setStates([]));
  }, []);

  // ── Fetch hospitals ────────────────────────────────────────────────────────
  const fetchHospitals = useCallback(async () => {
    try {
      setLoading(true);

      // 1. Get user profile and determine role / access scope
      const user = await AuthService.getCurrentUser();
      let isSuperAdmin = false;
      let allowedIds: any[] = [];

      if (user?.username) {
        const profile = await AuthService.getProfile(user.username);
        const role = profile?.role?.toLowerCase() || "";
        setUserRole(role);
        // role_id is NOT returned by the staff-users API; rely on the role string.
        // The DB stores "Admin" (capital A); toLowerCase() makes it "admin".
        isSuperAdmin = role === "admin";

        if (!isSuperAdmin) {
          // Guard with ?. — profile may be null if auth_uuid doesn't match Cognito username
          const locIds = profile?.location_ids || profile?.facility_ids || [];
          allowedIds = Array.isArray(locIds) ? locIds : [locIds];
          if (profile?.associated_branch) allowedIds.push(profile.associated_branch);
        }
      }

      // 2. Fetch all hospital / location data
      const data = await AuthService.getHospitalsAndBranches();

      // 3. Transform API response into Hospital[] grouped by hospital
      let transformed: Hospital[] = [];

      if (Array.isArray(data)) {
        const hospitalMap = new Map<string, Hospital>();

        // Flatten every nested location into a single allBranches list,
        // carrying hospital-level contact info forward from the parent item.
        const allBranches: any[] = [];

        data.forEach((item: any) => {
          // Backend GET /rds/lookups/hospitals returns STATE-grouped objects:
          // { state_id, state_name, locations: [ { id, hospital_id, hospital_name, address, ... } ] }
          const nested =
            Array.isArray(item.clinic_locations) ? item.clinic_locations :
            Array.isArray(item.locations)        ? item.locations :
            Array.isArray(item.branches)         ? item.branches : [];

          // For response shapes that put hospital info at the parent level
          const parentHId   = item.id || item.hospital_id || item.hospitalId;
          const parentHName = item.hospital_name || item.hospitalName || item.hospital?.name;

          if (nested.length > 0) {
            nested.forEach((loc: any) => {
              const bId = String(loc.id || loc.location_id || loc.branch_id || "");

              // Non-admin users only see their allowed locations
              if (!isSuperAdmin && !allowedIds.map((id: any) => String(id)).includes(bId)) {
                return;
              }

              allBranches.push({
                ...loc,
                hospital_id:      loc.hospital_id || loc.hospitalId || parentHId,
                hospital_name:    loc.hospital_name || parentHName || "Main Hospital",
                // Carry hospital-level contact info so cards can display it
                hospital_phone:   loc.hospital_phone || item.hospital_phone || item.phone,
                hospital_email:   loc.hospital_email || item.hospital_email || item.email,
                hospital_address: loc.hospital_address || item.hospital_address || item.address,
              });
            });
          } else if (item.id || item.location_id) {
            // Flat row (no nesting) — treat the item itself as a branch
            const bId = String(item.id || item.location_id || "");
            if (!isSuperAdmin && !allowedIds.map((id: any) => String(id)).includes(bId)) {
              return;
            }
            allBranches.push({
              ...item,
              hospital_id:   item.hospital_id || item.hospitalId || item.id,
              hospital_name: item.hospital_name || item.hospitalName || "Main Hospital",
            });
          }
        });

        // Group branches by hospital
        allBranches.forEach((branch: any) => {
          const hName = branch.hospital_name || "Main Hospital";
          const hId   = String(branch.hospital_id || hName);

          if (!hospitalMap.has(hId)) {
            hospitalMap.set(hId, {
              id:      hId,
              name:    hName,
              // Use hospital-level contact info when available; fall back to branch-level.
              // address_line1 is the primary field since the old address column was dropped.
              phone:   branch.hospital_phone   || branch.phone   || "",
              email:   branch.hospital_email   || branch.email   || "",
              address: branch.hospital_address || branch.address_line1 || branch.address || "",
              branches: [],
            });
          }

          const hospital = hospitalMap.get(hId);
          if (hospital) {
            const bId = String(branch.id || branch.location_id || branch.branch_id || "");
            if (bId && !hospital.branches?.find(existing => existing.id === bId)) {
              hospital.branches?.push({
                id:            bId,
                name:          branch.name || branch.branch_name || branch.branchName || "Main Branch",
                phone:         branch.phone  || "",
                email:         branch.email  || "",
                // Carry both legacy and split fields so the edit modal can pre-fill either
                address:       branch.address       || "",
                address_line1: branch.address_line1 || "",
                address_line2: branch.address_line2 || "",
                city:          branch.city          || "",
                zip:           branch.zip           || "",
                state_id:      branch.state_id      || undefined,
              });
            }
          }
        });

        transformed = Array.from(hospitalMap.values())
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
          .map(h => ({
            ...h,
            branches: (h.branches || []).sort((a: any, b: any) =>
              (a.name || "").localeCompare(b.name || "")
            ),
          }));
      }

      setHospitals(transformed);
    } catch (error) {
      console.error("Error fetching hospitals:", error);
      toast.error("Failed to load hospitals");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHospitals();
  }, [fetchHospitals]);

  // ── Branch helpers ─────────────────────────────────────────────────────────
  const handleAddBranch = () => {
    setCurrent(prev => ({
      ...prev,
      branches: [...prev.branches, { name: "", state_id: "", phone: "" }],
    }));
  };

  const handleRemoveBranch = (index: number) => {
    setCurrent(prev => ({
      ...prev,
      branches: prev.branches.filter((_, i) => i !== index),
    }));
  };

  const handleBranchStateChange = (index: number, value: string) => {
    setCurrent(prev => ({
      ...prev,
      branches: prev.branches.map((b, i) => i === index ? { ...b, state_id: value } : b),
    }));
  };

  const handleBranchPhoneInput = (index: number, e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    target.value = formatPhoneInput(target.value);
    setCurrent(prev => ({
      ...prev,
      branches: prev.branches.map((b, i) => i === index ? { ...b, phone: target.value } : b),
    }));
  };

  // ── Read all form values from DOM at save time ─────────────────────────────
  // Uncontrolled inputs (defaultValue) don't update React state on change, so
  // we grab their current DOM values here right before saving.
  const readFormValues = () => {
    const el = formRef.current;
    if (!el) return null;

    const val = (id: string) =>
      (el.querySelector(`#${id}`) as HTMLInputElement)?.value?.trim() || "";

    const hospitalName = val("h-name");

    const branches = current.branches.map((b, i) => ({
      id:            b.id,
      state_id:      b.state_id,
      phone:         b.phone,           // controlled state (formatted)
      name:          val(`bn-${i}`),
      address_line1: val(`ba1-${i}`),
      address_line2: val(`ba2-${i}`),
      city:          val(`bc-${i}`),
      zip:           val(`bz-${i}`),
      email:         val(`be-${i}`),
    }));

    return { hospitalName, branches };
  };

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = (values: ReturnType<typeof readFormValues>): boolean => {
    if (!values) return false;
    const errors: Record<string, string> = {};

    if (!values.hospitalName) errors.name = "Hospital name is required";

    values.branches.forEach((b, i) => {
      if (!b.name) errors[`branch_${i}_name`] = "Branch name is required";

      // Address fields: required for NEW branches only.
      // Existing branches (b.id set) may be old-schema rows without city/zip —
      // admin can update name/phone/email without being forced to re-enter address.
      if (!b.id) {
        if (!b.address_line1) errors[`branch_${i}_address_line1`] = "Address is required";
        if (!b.city)          errors[`branch_${i}_city`]          = "City is required";
        if (!b.state_id)      errors[`branch_${i}_state_id`]      = "State is required";
        if (!b.zip)           errors[`branch_${i}_zip`]           = "ZIP is required";
        else if (!isValidZip(b.zip)) errors[`branch_${i}_zip`]    = "Invalid ZIP code";
      } else {
        // For existing branches, still validate zip if admin filled it in
        if (b.zip && !isValidZip(b.zip)) errors[`branch_${i}_zip`] = "Invalid ZIP code";
        // State still required if admin is explicitly changing it
        // (it should already be set from the DB via branchStateFromApi)
      }

      if (b.phone && !isValidPhone(b.phone)) errors[`branch_${i}_phone`] = "Phone must be 10 digits";
      if (b.email && !b.email.includes("@")) errors[`branch_${i}_email`] = "Invalid email";
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSaveHospital = async () => {
    const values = readFormValues();
    if (!validate(values)) {
      toast.error("Please fix the highlighted errors before saving");
      return;
    }

    const { hospitalName, branches } = values!;
    const processingToast = toast.loading(isEditModalOpen ? "Updating hospital..." : "Creating hospital...");

    try {
      const hospitalPayload = { name: hospitalName };

      if (isEditModalOpen && current.id) {
        // Only non-BranchAdmin can update the hospital record itself
        if (!isBranchAdmin) {
          await AuthService.updateHospital(current.id, hospitalPayload);
        }

        // Derive a fallback state_id from the first existing branch that has one,
        // in case a new branch is added without explicitly selecting a state
        const fallbackStateId = branches.find(b => b.id && b.state_id)?.state_id || "";

        await Promise.all(branches.map(branch => {
          const payload = {
            name:          branch.name,
            email:         branch.email,
            phone:         branch.phone,
            address_line1: branch.address_line1,
            address_line2: branch.address_line2,
            city:          branch.city,
            zip:           branch.zip,
            state_id:      branch.state_id || fallbackStateId,
            hospital_id:   current.id,
          };

          if (branch.id) {
            // Existing branch — update
            return AuthService.updateBranch(branch.id, payload);
          } else {
            // New branch — create (requires state_id)
            if (!payload.state_id) {
              console.warn("Skipping new branch — no state_id available");
              return Promise.resolve();
            }
            return AuthService.createBranch(current.id!, payload);
          }
        }));

        toast.success("Facility records updated successfully", { id: processingToast });
      } else {
        // Create new hospital then create any branches added in the same modal
        const newHospital = await AuthService.createHospital(hospitalPayload);

        if (newHospital?.id && branches.length > 0) {
          const fallbackStateId = branches.find(b => b.state_id)?.state_id || "";
          await Promise.all(
            branches
              .filter(b => b.name && (b.state_id || fallbackStateId))
              .map(branch =>
                AuthService.createBranch(String(newHospital.id), {
                  name:          branch.name,
                  email:         branch.email,
                  phone:         branch.phone,
                  address_line1: branch.address_line1,
                  address_line2: branch.address_line2,
                  city:          branch.city,
                  zip:           branch.zip,
                  state_id:      branch.state_id || fallbackStateId,
                  hospital_id:   String(newHospital.id),
                })
              )
          );
        }

        toast.success("Hospital created successfully", { id: processingToast });
      }

      setIsAddModalOpen(false);
      setIsEditModalOpen(false);
      fetchHospitals();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("An error occurred while saving. Please check your network or try again.", { id: processingToast });
    }
  };

  // ── Open edit modal ────────────────────────────────────────────────────────
  const openEditModal = (hospital: Hospital) => {
    setCurrent({
      id:       hospital.id,
      name:     hospital.name || "",
      branches: (hospital.branches || []).map(branchStateFromApi),
    });
    setFormErrors({});
    setModalKey(k => k + 1);   // force DialogContent remount → uncontrolled inputs reset
    setIsEditModalOpen(true);
  };

  const closeModal = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setFormErrors({});
  };

  // ── Filtered hospital list ─────────────────────────────────────────────────
  const filteredHospitals = hospitals
    .filter(h =>
      h.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      h.email?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    .map(h => ({
      ...h,
      branches: [...(h.branches || [])].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      ),
    }));

  // ── Sub-components ─────────────────────────────────────────────────────────
  const FieldError = ({ id }: { id: string }) =>
    formErrors[id] ? <p className="text-xs text-red-500 mt-1">{formErrors[id]}</p> : null;

  const StateSelect = ({ value, onChange, id }: { value: string; onChange: (v: string) => void; id: string }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} className="h-10 border-gray-100 bg-gray-50 rounded-xl text-sm">
        <SelectValue placeholder="Select state" />
      </SelectTrigger>
      <SelectContent>
        {states.map(s => (
          <SelectItem key={s.state_id} value={s.state_id}>{s.state_name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <MainLayout>
      <div className="min-h-screen bg-[#f7f8fa] p-6 lg:p-10 font-sans">
        <div className="max-w-7xl mx-auto space-y-8">

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl lg:text-4xl font-extrabold text-[#1a7a4a] tracking-tight">
                {isBranchAdmin ? "Branch Management" : "Hospital & Branch Management"}
              </h1>
              <p className="text-gray-500 mt-2 text-lg">
                {isBranchAdmin
                  ? "Manage your assigned clinic branches"
                  : "Manage hospitals and their associated branches"}
              </p>
            </div>

            <div className="flex items-center gap-4">
              <div className="relative group">
                <input
                  type="text"
                  placeholder={isBranchAdmin ? "Search branches..." : "Search hospitals..."}
                  className="pl-12 pr-4 h-12 w-64 lg:w-80 bg-white border-gray-200 focus:border-[#1a7a4a] focus:ring-[#1a7a4a] rounded-2xl text-sm transition-all shadow-sm group-hover:shadow-md outline-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Search className="absolute left-4 top-3.5 h-5 w-5 text-gray-400 group-hover:text-[#1a7a4a] transition-colors" />
              </div>

              {!isBranchAdmin && (
                <Button
                  onClick={() => {
                    setCurrent(emptyHospital());
                    setFormErrors({});
                    setModalKey(k => k + 1);
                    setIsAddModalOpen(true);
                  }}
                  className="h-12 px-6 rounded-2xl bg-[#1a7a4a] hover:bg-[#15633c] text-white shadow-lg shadow-green-100 transition-all hover:scale-[1.02] flex items-center gap-2 font-bold"
                >
                  <Plus className="w-5 h-5" />
                  Add Hospital
                </Button>
              )}
            </div>
          </div>

          {/* Hospital Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              <div className="col-span-full p-20 flex flex-col items-center justify-center space-y-4">
                <Loader2 className="h-10 w-10 animate-spin text-[#1a7a4a]" />
                <p className="text-gray-500 font-medium animate-pulse">Loading facility records...</p>
              </div>
            ) : filteredHospitals.length > 0 ? (
              filteredHospitals.map(hospital => (
                <Card key={hospital.id} className="rounded-3xl border-gray-100 shadow-xl shadow-gray-200/50 bg-white overflow-hidden group hover:border-[#1a7a4a]/30 transition-all duration-300">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-2xl bg-[#1a7a4a]/10 flex items-center justify-center text-[#1a7a4a] font-bold text-xl">
                        <Building2 className="w-6 h-6" />
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditModal(hospital)}
                        className="rounded-full hover:bg-gray-100 text-gray-400 hover:text-[#1a7a4a]"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#1a7a4a] transition-colors mb-2">{hospital.name}</h3>

                    <div className="space-y-2 mb-6">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Mail className="w-4 h-4 text-gray-300" />
                        {hospital.email || "No email provided"}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Phone className="w-4 h-4 text-gray-300" />
                        {hospital.phone || "No phone provided"}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <MapPin className="w-4 h-4 text-gray-300 flex-shrink-0" />
                        <span className="truncate">{hospital.address || "No address provided"}</span>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-50">
                      <button
                        onClick={() => setExpandedHospitalId(
                          expandedHospitalId === hospital.id ? null : hospital.id
                        )}
                        className="w-full flex items-center justify-between text-sm font-bold text-[#1a7a4a] hover:text-[#15633c] transition-colors"
                      >
                        <span>{hospital.branches?.length || 0} Branches</span>
                        {expandedHospitalId === hospital.id
                          ? <ChevronDown className="w-4 h-4 transition-transform" />
                          : <ChevronRight className="w-4 h-4 transition-transform" />
                        }
                      </button>

                      {expandedHospitalId === hospital.id && (
                        <div className="mt-3 space-y-2">
                          {hospital.branches && hospital.branches.length > 0 ? (
                            hospital.branches.map((branch, idx) => (
                              <div
                                key={branch.id || idx}
                                className="flex items-start gap-2 p-2.5 rounded-xl bg-[#1a7a4a]/5 text-sm"
                              >
                                <MapPin className="w-3.5 h-3.5 text-[#1a7a4a] mt-0.5 flex-shrink-0" />
                                <div className="min-w-0">
                                  <p className="font-semibold text-gray-800 truncate">{branch.name}</p>
                                  {formatBranchAddress({
                                    ...branch,
                                    state_name: states.find(s => s.state_id === branch.state_id)?.state_name,
                                  }) && (
                                    <p className="text-xs text-gray-400 truncate">
                                      {formatBranchAddress({
                                        ...branch,
                                        state_name: states.find(s => s.state_id === branch.state_id)?.state_name,
                                      })}
                                    </p>
                                  )}
                                  {(branch.phone || branch.email) && (
                                    <p className="text-xs text-gray-400 truncate">
                                      {[branch.phone, branch.email].filter(Boolean).join(" · ")}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-xs text-gray-400 text-center py-2">No branches configured.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))
            ) : (
              <div className="col-span-full p-20 text-center border-2 border-dashed border-gray-100 rounded-3xl">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Building2 className="w-10 h-10 text-gray-200" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No hospitals found</h3>
                <p className="text-gray-500">Add your first hospital to start managing branches.</p>
              </div>
            )}
          </div>

          {/* Add / Edit Modal */}
          {/* key forces a full remount when switching between add and edit, so
              uncontrolled defaultValue inputs always reflect the current data. */}
          <Dialog open={isAddModalOpen || isEditModalOpen} onOpenChange={(val) => {
            if (!val) closeModal();
          }}>
            <DialogContent
              key={modalKey}
              className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto rounded-3xl p-0 border-none shadow-2xl"
            >
              <div className="p-8" ref={formRef}>
                <DialogHeader className="mb-8">
                  <DialogTitle className="text-2xl font-extrabold text-[#1a7a4a]">
                    {isEditModalOpen ? "Edit Hospital Profile" : "Create New Hospital"}
                  </DialogTitle>
                  <DialogDescription className="text-gray-500 text-lg">
                    Configure the hospital details and manage its associated branches below.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-8">

                  {/* ── Hospital details ── */}
                  <div className="space-y-5">
                    <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#1a7a4a] flex items-center gap-2 bg-[#1a7a4a]/5 p-2 px-4 rounded-lg w-fit">
                      <Plus className="w-3.5 h-3.5" />
                      Hospital Details
                    </h4>
                    <div className="space-y-2.5">
                      <Label htmlFor="h-name" className="text-xs font-bold text-gray-700 ml-1">
                        HOSPITAL NAME <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="h-name"
                        placeholder="e.g. Memorial General Hospital"
                        defaultValue={current.name}
                        disabled={isBranchAdmin}
                        className="rounded-2xl border-gray-100 bg-gray-50 focus:bg-white focus:border-[#1a7a4a] h-12 transition-all shadow-sm"
                      />
                      <FieldError id="name" />
                    </div>
                  </div>

                  {/* ── Branches ── */}
                  <div className="space-y-5">
                    <div className="flex items-center justify-between bg-gray-50 p-3 px-4 rounded-2xl">
                      <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5" />
                        Associated Branches
                      </h4>
                      {!isBranchAdmin && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleAddBranch}
                          className="text-[#1a7a4a] hover:bg-white font-bold flex items-center gap-2 px-4 rounded-xl shadow-sm border border-transparent hover:border-[#1a7a4a]/20 transition-all"
                        >
                          <Plus className="w-4 h-4" />
                          Add Branch
                        </Button>
                      )}
                    </div>

                    <div className="space-y-4 max-h-[420px] overflow-y-auto pr-2 p-1">
                      {current.branches.map((branch, index) => (
                        <div key={branch.id || index} className="p-5 rounded-3xl border border-gray-100 bg-white shadow-sm relative group/branch hover:border-[#1a7a4a]/30 transition-all">
                          {!isBranchAdmin && (
                            <button
                              onClick={() => handleRemoveBranch(index)}
                              className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-100 shadow-sm flex items-center justify-center opacity-0 group-hover/branch:opacity-100 transition-all z-10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}

                          <div className="flex items-center gap-2 mb-4">
                            <span className="text-xs font-bold text-[#1a7a4a] bg-[#1a7a4a]/5 px-3 py-1 rounded-full">
                              Branch {index + 1}{branch.id ? "" : " (New)"}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-4">

                            {/* Branch name */}
                            <div className="col-span-2 space-y-2">
                              <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">
                                Branch Name <span className="text-red-500">*</span>
                              </Label>
                              <Input
                                id={`bn-${index}`}
                                placeholder="e.g. West Wing Mental Health Clinic"
                                defaultValue={branch.name}
                                className="bg-gray-50 border-transparent focus:border-[#1a7a4a]/30 focus:bg-white text-sm font-bold h-10 rounded-xl transition-all"
                              />
                              <FieldError id={`branch_${index}_name`} />
                            </div>

                            {/* Address Line 1 */}
                            <div className="col-span-2 space-y-2">
                              <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">
                                Address Line 1{!branch.id && <span className="text-red-500"> *</span>}
                              </Label>
                              <Input
                                id={`ba1-${index}`}
                                placeholder="123 Medical Center Dr"
                                defaultValue={branch.address_line1 ?? branch.address ?? ""}
                                className="bg-gray-50 border-transparent focus:border-[#1a7a4a]/30 focus:bg-white text-xs h-10 rounded-xl transition-all"
                              />
                              <FieldError id={`branch_${index}_address_line1`} />
                            </div>

                            {/* Address Line 2 */}
                            <div className="col-span-2 space-y-2">
                              <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">Address Line 2</Label>
                              <Input
                                id={`ba2-${index}`}
                                placeholder="Suite / Floor (optional)"
                                defaultValue={branch.address_line2 ?? ""}
                                className="bg-gray-50 border-transparent focus:border-[#1a7a4a]/30 focus:bg-white text-xs h-10 rounded-xl transition-all"
                              />
                            </div>

                            {/* City */}
                            <div className="space-y-2">
                              <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">
                                City{!branch.id && <span className="text-red-500"> *</span>}
                              </Label>
                              <Input
                                id={`bc-${index}`}
                                placeholder="City"
                                defaultValue={branch.city ?? ""}
                                className="bg-gray-50 border-transparent focus:border-[#1a7a4a]/30 focus:bg-white text-xs h-10 rounded-xl transition-all"
                              />
                              <FieldError id={`branch_${index}_city`} />
                            </div>

                            {/* State */}
                            <div className="space-y-2">
                              <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">
                                State{!branch.id && <span className="text-red-500"> *</span>}
                              </Label>
                              <StateSelect
                                id={`bs-${index}`}
                                value={branch.state_id}
                                onChange={v => handleBranchStateChange(index, v)}
                              />
                              <FieldError id={`branch_${index}_state_id`} />
                            </div>

                            {/* ZIP */}
                            <div className="space-y-2">
                              <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">
                                ZIP{!branch.id && <span className="text-red-500"> *</span>}
                              </Label>
                              <Input
                                id={`bz-${index}`}
                                placeholder="12345"
                                maxLength={10}
                                defaultValue={branch.zip ?? ""}
                                className="bg-gray-50 border-transparent focus:border-[#1a7a4a]/30 focus:bg-white text-xs h-10 rounded-xl transition-all"
                              />
                              <FieldError id={`branch_${index}_zip`} />
                            </div>

                            {/* Phone */}
                            <div className="space-y-2">
                              <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">Branch Phone</Label>
                              <div className="relative flex items-center">
                                <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                <span className="absolute left-7 top-1/2 -translate-y-1/2 text-gray-400 font-medium text-xs pointer-events-none select-none">+1</span>
                                <Input
                                  type="text"
                                  id={`bp-${index}`}
                                  placeholder="###-###-####"
                                  value={branch.phone}
                                  onInput={e => handleBranchPhoneInput(index, e)}
                                  onChange={() => {}}
                                  maxLength={12}
                                  className="h-10 pl-12 bg-gray-50 border-transparent focus:border-[#1a7a4a]/30 focus:bg-white text-xs rounded-xl transition-all"
                                />
                              </div>
                              <FieldError id={`branch_${index}_phone`} />
                            </div>

                            {/* Email */}
                            <div className="space-y-2">
                              <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">Branch Email</Label>
                              <Input
                                id={`be-${index}`}
                                type="email"
                                placeholder="branch@hospital.com"
                                defaultValue={branch.email ?? ""}
                                className="bg-gray-50 border-transparent focus:border-[#1a7a4a]/30 focus:bg-white text-xs h-10 rounded-xl transition-all"
                              />
                              <FieldError id={`branch_${index}_email`} />
                            </div>

                          </div>
                        </div>
                      ))}

                      {current.branches.length === 0 && (
                        <div className="text-center py-10 border-2 border-dashed border-gray-100 rounded-3xl bg-gray-50/30">
                          <MapPin className="w-8 h-8 text-gray-200 mx-auto mb-3" />
                          <p className="text-gray-400 text-sm font-medium">No branches configured for this hospital.</p>
                          <p className="text-gray-300 text-[11px] mt-1">Click the "Add Branch" button to register a new branch or location.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-10 flex gap-4">
                  <Button
                    variant="ghost"
                    onClick={closeModal}
                    className="flex-1 h-12 rounded-2xl text-gray-400 hover:text-gray-600 font-bold hover:bg-gray-50"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveHospital}
                    className="flex-[2] h-12 rounded-2xl bg-[#1a7a4a] hover:bg-[#15633c] text-white font-bold shadow-lg shadow-green-100/50 flex items-center justify-center gap-2 transition-all hover:scale-[1.01]"
                  >
                    <Save className="w-5 h-5" />
                    {isEditModalOpen ? "Update Hospital" : "Deploy New Hospital"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

        </div>
      </div>
    </MainLayout>
  );
}
